import type { PaginatedList } from '../types/api/common';

const id = (n: number) => `00000000-0000-4000-8000-${String(n).padStart(12, '0')}`;

/** Default admin credentials for mock / local development */
export const ADMIN_CREDENTIALS = {
  email: 'saungadmin@gmail.com',
  password: 'password',
} as const;

export const MOCK_ADMIN = {
  userId: id(1),
  adminProfileId: id(2),
  email: ADMIN_CREDENTIALS.email,
  fullName: 'SAUNG Admin',
  accessLevel: 'SUPER_ADMIN' as const,
  department: 'Operations',
  permissions: {
    manage_users: true,
    view_reports: true,
  },
  isActive: true,
  lastLoginAt: new Date().toISOString(),
};

export const MOCK_TOKENS = {
  accessToken: 'mock-saung-access-token',
  refreshToken: 'mock-saung-refresh-token',
  tokenType: 'Bearer',
  expiresIn: 86400,
};

function paginate<T>(items: T[], page = 0, size = 10): PaginatedList<T> {
  const start = page * size;
  const slice = items.slice(start, start + size);
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / size));
  return {
    items: slice,
    page,
    size,
    totalItems,
    totalPages,
    hasNext: page + 1 < totalPages,
    hasPrevious: page > 0,
  };
}

export const MOCK_MOVIES = Array.from({ length: 8 }, (_, i) => ({
  contentId: id(100 + i),
  title: `Sample Movie ${i + 1}`,
  description: 'Mock movie for SAUNG dashboard development.',
  thumbnailUrl: '',
  uploadDate: new Date().toISOString(),
  languageId: id(10),
  languageName: 'Myanmar',
  accessLevel: 'FREE' as const,
  status: 'ACTIVE' as const,
  director: 'Director Name',
  runtime: 120,
  priority: i + 1,
  releaseDate: '2024-01-15',
}));

export const MOCK_ALBUMS = Array.from({ length: 6 }, (_, i) => ({
  albumId: id(200 + i),
  name: `Album ${i + 1}`,
  albumType: 'myanmar' as const,
  priority: i + 1,
  releaseDate: '2024-06-01',
  coverArtUrl: null,
  imageUrl: null,
  description: 'Mock album',
  musicVideoCount: 3,
}));

export const MOCK_USERS = Array.from({ length: 5 }, (_, i) => ({
  userId: id(300 + i),
  email: `user${i + 1}@example.com`,
  fullName: `User ${i + 1}`,
  accessLevel: 'CUSTOMER',
  tier: 'FREE',
  isActive: true,
  createdAt: new Date().toISOString(),
}));

export const MOCK_LANGUAGES = Array.from({ length: 4 }, (_, i) => ({
  languageId: id(400 + i),
  name: ['Myanmar', 'English', 'Korean', 'Japanese'][i],
  code: ['my', 'en', 'ko', 'ja'][i],
  isActive: true,
}));

export const MOCK_GENRES = Array.from({ length: 6 }, (_, i) => ({
  genreId: id(500 + i),
  name: ['Pop', 'Rock', 'Hip-Hop', 'R&B', 'Classical', 'Electronic'][i],
  nameMm: `Genre ${i + 1}`,
  iconUrl: '',
  colorCode: '#EAB308',
  isActive: true,
}));

export const MOCK_SONGS = Array.from({ length: 10 }, (_, i) => ({
  trackId: id(600 + i),
  title: `Track ${i + 1}`,
  artistName: `Artist ${(i % 3) + 1}`,
  albumName: `Album ${(i % 2) + 1}`,
  durationSeconds: 180 + i * 10,
  status: 'ACTIVE' as const,
  releaseDate: '2025-01-01',
}));

export const MOCK_ARTISTS = Array.from({ length: 6 }, (_, i) => ({
  artistId: id(700 + i),
  name: `Artist ${i + 1}`,
  status: 'ACTIVE' as const,
  trackCount: 5 + i,
}));

export const MOCK_PLAYLISTS = Array.from({ length: 5 }, (_, i) => ({
  playlistId: id(800 + i),
  name: `Playlist ${i + 1}`,
  trackCount: 12 + i,
  isPublic: true,
  status: 'ACTIVE' as const,
}));

export const MOCK_MUSIC_VIDEOS = Array.from({ length: 6 }, (_, i) => ({
  musicVideoId: id(900 + i),
  title: `Music Video ${i + 1}`,
  artistId: id(700 + (i % 3)),
  artistName: `Artist ${(i % 3) + 1}`,
  albumName: i % 2 === 0 ? `Album ${i + 1}` : undefined,
  durationSeconds: 200 + i * 15,
  accessLevel: 'FREE' as const,
  status: 'ACTIVE' as const,
  thumbnailUrl: null,
  videoUrl: null,
}));

export { paginate };
